import { Component, input, ElementRef, viewChild, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { NodeModel, DragDrop, RegistryEntry } from '@xanadu/model';
import { MenuData } from '@xanadu/menu';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { TabSignal, TabSignalSet, TreeSignal } from '@xanadu/signal';

@Component({
    selector: 'node',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class NodeComponent {
    protected readonly location = inject(Location);
    protected readonly document = inject(DOCUMENT);
    protected readonly element = inject(ElementRef);
    protected readonly treeSignal = inject(TreeSignal);
    protected readonly tabSignal = inject(TabSignal);

    protected get nodeType(): string | undefined
    {
        return undefined;
    }

    model = input<NodeModel>();
    nameField = viewChild<ElementRef>('name');
    icon?: string;

    constructor() {
        effect(() => {
            const load = this.tabSignal.load[this.nodeType as keyof TabSignalSet<NodeModel>]();
            const model = this.model();

            if (load && model === load) {
                this.tabSignal.enter[this.nodeType as keyof TabSignalSet<NodeModel>].set(new RegistryEntry(load, this.element.nativeElement));
            }
        });
    }

    onToggle(): void {
        const model = this.model();

        if (model) {
            model.toggleSolo();
            this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>].set(model);
            this.location.go(`${this.nodeType}/id/${model.node._id}`);
        }
    }

    onBlur(): void {
    }

    onEdit(): void {
        const model = this.model();
        const nameField = this.nameField();

        model?.edit();

        if (nameField) {
            nameField?.nativeElement?.focus();
        }
    }

    onSave(): void {
        this.model()?.save();
    }

    onCancel(): void {
        this.model()?.cancel();
    }

    onMenu(event: MouseEvent) {
        event.preventDefault();
        const data = new MenuData();
        data.position = { x: event.clientX, y: event.clientY };
        data.viewModel = this.model();
        this.tabSignal.menu[this.nodeType as keyof TabSignalSet<MenuData>].set(data);
    }

    onFocus(): void {
        const model = this.model();

        if (model && !model.solo.active) {
            this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>].set(model);
            this.location.go(`${this.nodeType}/id/${model.node._id}`);
        }
    }

    onDrop(event: CdkDragDrop<any>) {
        const selectorClass = `${this.nodeType}-node`;
        const dragModel = event.item.data as NodeModel
        const target = this.document.elementFromPoint(event.dropPoint.x, event.dropPoint.y);
        let container = target?.classList.contains(selectorClass) ? target : target?.closest(`.${selectorClass}`);

        if (container?.id && dragModel.node._id !== container.id) {
            const dragDrop = new DragDrop(dragModel, container.id);
            this.tabSignal.drop[this.nodeType as keyof TabSignalSet<DragDrop>].set(dragDrop);
        }
    }

    onNameChange(name: string) {
        const model = this.model();

        if (model?.node) {
            model.node.name = name;
        }
    }
}
